import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  m as motion,
  MAnimatePresence as AnimatePresence,
} from "../../shared/components/Motion";
import "./styles/themes.css";
import ColorPicker from "./ColorPicker";
import { ToggleSwitch, ButtonComponent } from "shared/components";
import {
  modalVariantsForThemes,
  childVariants,
  accordionContainer,
  accordionWrapper,
  accordionItem,
} from "../../shared/utils";
import ColorProperties from "./ColorProperties";
import CLEAR_THEME from "./DefaultThemes.jsx";
import { useDefaultThemesData } from "../../shared/hooks";
import { useAdmin } from "../../features/admin/AdminProvider";
import EditDefaultThemesModal from "../../features/admin/modals/EditDefaultThemesModal";
import SegmentedControl from "./SegmentedControl";
import {
  useAnimationMode,
  useSetAnimationMode,
} from "../../shared/context/AnimationContext";
import { THEME_CONFIG } from "config/themeConfig";

import "./styles/glass.css"; // Import Glass Styles

const Themes = () => {
  const {
    themesPanelOpen,
    toggleThemesPanel,
    closeThemesPanel,
    isAdmin,
    defaultThemesDraft,
    saveDefaultThemesDraft,
  } = useAdmin();
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [, setShowResetOverlay] = useState(false);

  // Fetch default themes from Firebase with caching
  const {
    data: firebaseThemesData,
  } = useDefaultThemesData();

  // const isDefaultThemesLoading =
  //   firebaseThemesNetworkLoading || showResetOverlay;

  // Combine CLEAR_THEME (always first) with Firebase themes
  // Fallback to just CLEAR_THEME if Firebase data hasn't loaded yet
  let storedDefaultThemes = [
    CLEAR_THEME,
    ...(firebaseThemesData?.themes || []),
  ];

  let getStoredCustomThemes = localStorage.getItem("storedCustomThemes");
  let storedCustomThemes;
  if (getStoredCustomThemes) {
    storedCustomThemes = JSON.parse(getStoredCustomThemes);
  }

  const [activeThemeInCustomThemes, setActiveThemeInCustomThemes] = useState(
    () => {
      const storedActiveThemeInCustomThemes = localStorage.getItem(
        "activeThemeInCustomThemes",
      );
      return storedActiveThemeInCustomThemes !== null
        ? parseInt(storedActiveThemeInCustomThemes)
        : THEME_CONFIG.activeThemeInCustomThemes;
    },
  );
  const [theme, setTheme] = useState(
    () => localStorage.getItem("theme") || THEME_CONFIG.theme,
  );

  const [enableColors, setEnableColors] = useState(() => {
    const raw = localStorage.getItem("enableColors");
    return raw !== null ? JSON.parse(raw) : THEME_CONFIG.enableColors;
  });

  const [colors, setColors] = useState(() => {
    const storedColors = {};
    ColorProperties.forEach((property) => {
      storedColors[property.name] = localStorage.getItem(property.name) || "";
    });
    return storedColors;
  });

  // setting default page load theme index from THEME_CONFIG
  const [activeThemeInDefaultThemes, setActiveThemeInDefaultThemes] = useState(
    () => {
      const storedActiveThemeInDefaultThemes = localStorage.getItem(
        "activeThemeInDefaultThemes",
      );
      return storedActiveThemeInDefaultThemes !== null
        ? parseInt(storedActiveThemeInDefaultThemes)
        : THEME_CONFIG.activeThemeInDefaultThemes;
    },
  );

  const [useColorMixBg, setUseColorMixBg] = useState(() => {
    const raw = localStorage.getItem("useColorMixBg");
    return raw !== null ? JSON.parse(raw) : THEME_CONFIG.useColorMixBg;
  });

  const [colorMixPercent, setColorMixPercent] = useState(() => {
    const raw = localStorage.getItem("colorMixPercent");
    return raw !== null ? parseInt(raw) : THEME_CONFIG.colorMixPercent;
  });

  const [darkModeIntensity, setDarkModeIntensity] = useState(
    () => localStorage.getItem("darkModeIntensity") || THEME_CONFIG.darkModeIntensity,
  );

  const [lightModeIntensity, setLightModeIntensity] = useState(
    () => localStorage.getItem("lightModeIntensity") || THEME_CONFIG.lightModeIntensity,
  );

  // --- Liquid Glass State ---
  const [glassEnabled, setGlassEnabled] = useState(() => {
    const raw = localStorage.getItem("glassEnabled");
    return raw !== null ? JSON.parse(raw) : THEME_CONFIG.glassEnabled;
  });
  const [glassBlur, setGlassBlur] = useState(() => {
    const raw = localStorage.getItem("glassBlur");
    return raw !== null ? parseInt(raw) : THEME_CONFIG.glassBlur;
  });
  const [glassOpacity, setGlassOpacity] = useState(() => {
    const raw = localStorage.getItem("glassOpacity");
    return raw !== null ? parseFloat(raw) : THEME_CONFIG.glassOpacity;
  });
  const [glassSaturation, setGlassSaturation] = useState(() => {
    const raw = localStorage.getItem("glassSaturation");
    return raw !== null ? parseInt(raw) : THEME_CONFIG.glassSaturation;
  });

  const [glassCursorEnabled, setGlassCursorEnabled] = useState(() => {
    const raw = localStorage.getItem("glassCursorEnabled");
    return raw !== null ? JSON.parse(raw) : THEME_CONFIG.glassCursorEnabled;
  });

  // Animation mode from context
  const animationMode = useAnimationMode();
  const setAnimationMode = useSetAnimationMode();
  const isAnimationEnabled = animationMode !== "none";

  // Remember the last non-none mode so toggling off then back on restores it
  const prevAnimModeRef = useRef(
    animationMode !== "none" ? animationMode : THEME_CONFIG.animationMode,
  );

  const handleAnimationToggle = () => {
    if (isAnimationEnabled) {
      prevAnimModeRef.current = animationMode; // save before turning off
      setAnimationMode("none");
    } else {
      setAnimationMode(prevAnimModeRef.current); // restore saved mode
      openSection("animation"); // auto-expand the segmented-control accordion
    }
  };

  const handleAnimationModeChange = (value) => {
    setAnimationMode(value);
  };

  const [expandedSection, setExpandedSection] = useState(
    () => new Set(THEME_CONFIG.defaultExpandedSections),
  );

  // Toggle a single accordion key in/out of the open Set
  const toggleSection = (key) =>
    setExpandedSection((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Open a section without closing others
  const openSection = (key) =>
    setExpandedSection((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });

  // Glass Background Textures
  const glassBackgroundTextures = useMemo(
    () => [
      {
        id: "clear",
        name: "Clear",
        url: null,
      },
      {
        id: "texture1",
        name: "Marble",
        url: "https://res.cloudinary.com/dc2dtlr4u/image/upload/f_auto,q_auto,w_1920/texture1_wupql5",
      },
      {
        id: "texture2",
        name: "Colorful",
        url: "https://res.cloudinary.com/dc2dtlr4u/image/upload/f_auto,q_auto,w_1920/texture2_aycefo",
      },
      {
        id: "texture3",
        name: "Gradient",
        url: "https://res.cloudinary.com/dc2dtlr4u/image/upload/f_auto,q_auto,w_1920/texture3_smkbdt",
      },
      {
        id: "texture4",
        name: "Gradient2",
        url: "https://res.cloudinary.com/dc2dtlr4u/image/upload/f_auto,q_auto,w_1920/texture4_rnndqr",
      },
      {
        id: "texture5",
        name: "Gradient3",
        url: "https://res.cloudinary.com/dc2dtlr4u/image/upload/f_auto,q_auto,w_1920/texture5_vqlqbx",
      },
    ],
    [],
  );

  const [selectedGlassTexture, setSelectedGlassTexture] = useState(
    () => localStorage.getItem("selectedGlassTexture") || THEME_CONFIG.selectedGlassTexture,
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const handleThemeChange = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
  };

  // Close on click outside the panel (same pattern as chatbot)
  useEffect(() => {
    if (!themesPanelOpen) return;
    const handlePointerDown = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        closeThemesPanel();
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [themesPanelOpen, closeThemesPanel]);

  const applyDefaultTheme = (themeColors, index) => {
    setColors(themeColors);
    setActiveThemeInDefaultThemes(index);
    ColorProperties.forEach((property) => {
      localStorage.setItem(property.name, themeColors[property.name]);
    });
    localStorage.setItem("enableColors", enableColors);
    localStorage.setItem("activeThemeInDefaultThemes", index);
  };
  useEffect(() => {
    if (storedCustomThemes) {
      if (enableColors) {
        const storedThemeColors = {};
        ColorProperties.forEach((property) => {
          storedThemeColors[property.name] =
            localStorage.getItem(property.name) || "";
        });
        applyCustomTheme(storedThemeColors, activeThemeInCustomThemes);
      } else {
        applyCustomTheme(
          storedCustomThemes[activeThemeInCustomThemes].colors,
          activeThemeInCustomThemes,
        );
      }
    }
    // eslint-disable-next-line
  }, [activeThemeInCustomThemes]);
  useEffect(() => {
    if (enableColors) {
      const storedThemeColors = {};
      ColorProperties.forEach((property) => {
        storedThemeColors[property.name] =
          localStorage.getItem(property.name) || "";
      });
      applyDefaultTheme(storedThemeColors, activeThemeInDefaultThemes);
    } else {
      // Safely access theme colors with fallback
      const activeTheme = storedDefaultThemes[activeThemeInDefaultThemes];
      if (activeTheme && activeTheme.colors) {
        applyDefaultTheme(activeTheme.colors, activeThemeInDefaultThemes);
      }
    }
    // eslint-disable-next-line
  }, [activeThemeInDefaultThemes, firebaseThemesData, enableColors]);

  useEffect(() => {
    const applyColors = () => {
      ColorProperties.forEach((property) => {
        document.documentElement.style.setProperty(
          property.cssVar,
          colors[property.name],
        );
      });
    };

    applyColors();
  }, [colors]);

  const applyCustomTheme = useCallback(
    (themeColors, index) => {
      setColors(themeColors);
      setActiveThemeInCustomThemes(index);
      ColorProperties.forEach((property) => {
        localStorage.setItem(property.name, themeColors[property.name]);
      });
      localStorage.setItem("enableColors", enableColors);
      localStorage.setItem("activeThemeInCustomThemes", index);
    },
    [enableColors, setColors, setActiveThemeInCustomThemes],
  );

  useEffect(() => {
    localStorage.setItem("useColorMixBg", JSON.stringify(useColorMixBg));
    localStorage.setItem("colorMixPercent", colorMixPercent);

    // Set CSS variable for glass.css to use
    document.documentElement.style.setProperty(
      "--colorMixPercent",
      `${colorMixPercent}%`,
    );

    // Also set data attribute for selector matching
    if (useColorMixBg) {
      document.documentElement.setAttribute("data-color-mix-bg", "true");
      document.body.style.backgroundColor = `color-mix(in srgb, var(--title-color) ${colorMixPercent}%, var(--body-color))`;

      const header = document.querySelector(".header");
      if (header) {
        header.style.backgroundColor = `color-mix(in srgb, var(--title-color) ${colorMixPercent}%, var(--body-color))`;
      }

      const contactForms = document.querySelectorAll(".contact__form-tag");
      contactForms.forEach((form) => {
        form.style.backgroundColor = `color-mix(in srgb, var(--title-color) ${colorMixPercent}%, var(--body-color))`;
      });
    } else {
      document.documentElement.removeAttribute("data-color-mix-bg");
      document.body.style.backgroundColor = "var(--body-color)";

      const header = document.querySelector(".header");
      if (header) {
        header.style.backgroundColor = "var(--header-color)";
      }

      const contactForms = document.querySelectorAll(".contact__form-tag");
      contactForms.forEach((form) => {
        form.style.backgroundColor = "var(--body-color)";
      });
    }
  }, [useColorMixBg, colorMixPercent, theme]);

  useEffect(() => {
    localStorage.setItem("darkModeIntensity", darkModeIntensity);

    if (theme === "dark") {
      const intensityColors = {
        soft: { bg: "#444", container: "#444" },
        balanced: { bg: "#333", container: "#333" },
        deep: { bg: "#000", container: "#0a0a0a" },
      };

      const colors =
        intensityColors[darkModeIntensity] || intensityColors.balanced;
      document.documentElement.style.setProperty(
        "--dark-background-color",
        colors.bg,
      );
      document.documentElement.style.setProperty(
        "--dark-container-color",
        colors.container,
      );
    }
  }, [darkModeIntensity, theme]);

  useEffect(() => {
    localStorage.setItem("lightModeIntensity", lightModeIntensity);

    if (theme === "light") {
      const intensityColors = {
        soft: { bg: "#e8e8e8", container: "#e8e8e8" },
        balanced: { bg: "#f5f5f5", container: "#f5f5f5" },
        deep: { bg: "#fff", container: "#fff" },
      };

      const colors =
        intensityColors[lightModeIntensity] || intensityColors.balanced;
      document.documentElement.style.setProperty(
        "--light-background-color",
        colors.bg,
      );
      document.documentElement.style.setProperty(
        "--light-container-color",
        colors.container,
      );
    }
  }, [lightModeIntensity, theme]);

  const handleColorMixBgChange = () => {
    setUseColorMixBg((prev) => {
      if (!prev) openSection("colorMix");
      return !prev;
    });
  };

  const handleGlassCursorChange = () => {
    setGlassCursorEnabled((prev) => !prev);
  };

  // Clears all theme/preference keys from localStorage and reloads.
  // Preserves content-cache keys managed by the Admin panel (portfolio_local_db)
  // and the admin auth flag (admin_mode) so they are unaffected.
  const handleResetThemeSettings = () => {
    sessionStorage.setItem("resetThemeInProgress", "true");

    const PRESERVE_KEYS = ["portfolio_local_db", "admin_mode"];
    Object.keys(localStorage)
      .filter((key) => !PRESERVE_KEYS.includes(key))
      .forEach((key) => localStorage.removeItem(key));

    window.location.reload();
  };

  useEffect(() => {
    if (sessionStorage.getItem("resetThemeInProgress")) {
      setShowResetOverlay(true);
      sessionStorage.removeItem("resetThemeInProgress");
      window.setTimeout(() => {
        setShowResetOverlay(false);
      }, 400);
    }
  }, []);

  // --- Glass Effect Effects ---
  useEffect(() => {
    localStorage.setItem("glassEnabled", glassEnabled);
    localStorage.setItem("glassBlur", glassBlur);
    localStorage.setItem("glassOpacity", glassOpacity);
    localStorage.setItem("glassSaturation", glassSaturation);

    if (glassEnabled) {
      document.documentElement.setAttribute("data-glass", "true");
      document.documentElement.style.setProperty(
        "--glass-blur",
        `${glassBlur}px`,
      );
      document.documentElement.style.setProperty(
        "--glass-opacity",
        glassOpacity,
      );
      document.documentElement.style.setProperty(
        "--glass-saturation",
        `${glassSaturation}%`,
      );
    } else {
      document.documentElement.removeAttribute("data-glass");
    }
  }, [glassEnabled, glassBlur, glassOpacity, glassSaturation]);

  // Turn off glass cursor when glass effect is disabled
  useEffect(() => {
    if (!glassEnabled) {
      setGlassCursorEnabled(false);
    }
  }, [glassEnabled]);

  // --- Glass Cursor Effect ---
  useEffect(() => {
    localStorage.setItem(
      "glassCursorEnabled",
      JSON.stringify(glassCursorEnabled),
    );

    // Dispatch custom event for GlassCursor component to listen to
    window.dispatchEvent(
      new CustomEvent("glassCursorToggle", {
        detail: { enabled: glassCursorEnabled },
      }),
    );
  }, [glassCursorEnabled]);

  // Handle Glass Texture Change
  useEffect(() => {
    localStorage.setItem("selectedGlassTexture", selectedGlassTexture);

    const selectedTexture = glassBackgroundTextures.find(
      (t) => t.id === selectedGlassTexture,
    );
    if (selectedTexture && selectedTexture.url) {
      document.documentElement.style.setProperty(
        "--glass-bg-image",
        `url(${selectedTexture.url})`,
      );
      document.documentElement.setAttribute("data-glass-texture", "true");
    } else {
      document.documentElement.removeAttribute("data-glass-texture");
    }
  }, [selectedGlassTexture, glassBackgroundTextures]);

  return (
    <>
      {/* {isDefaultThemesLoading && (
        <div className="themes__page-loading-overlay">
          <div className="themes__spinner">
            <i className="bx bx-loader"></i>
          </div>
        </div>
      )} */}
      <button
        ref={triggerRef}
        title="themes"
        type="button"
        className="themes__button"
        onClick={toggleThemesPanel}
      >
        {!themesPanelOpen ? (
          <i className="bx bx-palette themes__icon"></i>
        ) : (
          <i className="bx bx-x themes__icon"></i>
        )}
      </button>
      <AnimatePresence>
        {themesPanelOpen && (
          <>
            <motion.div
              ref={panelRef}
              className="themes__model active-model"
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={modalVariantsForThemes}
              style={{ zIndex: 1000 }}
            >
              {/** Render Dark Mode Toggle */}
              <motion.div
                className="themes__section active__color-mix"
                variants={childVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <div className="themes__model-content">
                  <ToggleSwitch
                    id="DarkModeCheckbox"
                    modelTitle={
                      theme === "light"
                        ? "Enable Dark Mode"
                        : "Disable Dark Mode"
                    }
                    checked={theme === "dark"}
                    onChange={handleThemeChange}
                    icons={["bx bxs-moon ", "bx bxs-sun "]}
                    isExpanded={expandedSection.has("darkMode")}
                    onToggleExpand={() => toggleSection("darkMode")}
                  />
                </div>
                {/** Render Theme Intensity Segmented Control */}
                <AnimatePresence mode="wait">
                  {expandedSection.has("darkMode") && (
                    <motion.div
                      variants={accordionContainer}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <motion.div
                        key={`${theme}-intensity`}
                        variants={accordionWrapper}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        style={{ marginTop: "10px" }}
                      >
                        <SegmentedControl
                          layoutId="theme-intensity-bg"
                          options={[
                            { label: "Soft", value: "soft" },
                            { label: "Balanced", value: "balanced" },
                            { label: "Deep", value: "deep" },
                          ]}
                          selected={
                            theme === "light"
                              ? lightModeIntensity
                              : darkModeIntensity
                          }
                          onChange={
                            theme === "light"
                              ? setLightModeIntensity
                              : setDarkModeIntensity
                          }
                        />
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/** Render Animation Mode Toggle */}
              <motion.div
                className={`themes__section ${
                  isAnimationEnabled ? "active__color-mix" : ""
                }`}
                variants={childVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <div className="themes__model-content">
                  <ToggleSwitch
                    id="AnimationModeCheckbox"
                    modelTitle={
                      isAnimationEnabled ? "Animations On" : "Animations Off"
                    }
                    checked={isAnimationEnabled}
                    onChange={handleAnimationToggle}
                    icons={["bx bx-fast-forward", "bx bx-skip-next"]}
                    isExpanded={expandedSection.has("animation")}
                    onToggleExpand={
                      isAnimationEnabled
                        ? () => toggleSection("animation")
                        : undefined
                    }
                  />
                </div>
                {/** Render Animation Mode Segmented Control */}
                <AnimatePresence mode="wait">
                  {isAnimationEnabled && expandedSection.has("animation") && (
                    <motion.div
                      variants={accordionContainer}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <motion.div
                        key="animation-mode"
                        variants={accordionWrapper}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        style={{ marginTop: "10px" }}
                      >
                        <SegmentedControl
                          layoutId="animation-mode-bg"
                          options={[
                            {
                              label: "Native Motion",
                              subtitle: "Faster & Lighter",
                              title:
                                "Uses CSS animations powered by the GPU. No JavaScript on the main thread — stays smooth even when the page is busy.",
                              value: "css",
                            },
                            {
                              label: "Framer Motion",
                              subtitle: "Smoother & Heavier",
                              title:
                                "Uses Framer Motion's JavaScript engine for richer, spring-based animations. May feel slightly heavier on low-end devices.",
                              value: "framer-motion",
                            },
                          ]}
                          selected={animationMode}
                          onChange={handleAnimationModeChange}
                        />
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/** --- Liquid Glass Section --- */}
              <motion.div
                className={`themes__section ${
                  glassEnabled ? "active__color-mix" : ""
                }`}
                variants={childVariants}
              >
                <motion.div
                  className="themes__model-content"
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <ToggleSwitch
                    id="GlassEffectCheckbox"
                    modelTitle={"Glass Effect"}
                    checked={glassEnabled}
                    onChange={() => setGlassEnabled(!glassEnabled)}
                    icons={["bx bx-water", "bx bx-droplet"]}
                    isExpanded={expandedSection.has("glass")}
                    onToggleExpand={
                      glassEnabled ? () => toggleSection("glass") : undefined
                    }
                  />
                </motion.div>

                <AnimatePresence mode="wait">
                  {glassEnabled && expandedSection.has("glass") && (
                    <motion.div
                      variants={accordionContainer}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <motion.div
                        variants={accordionWrapper}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                        }}
                      >
                        <motion.div
                          className="themes__model-content"
                          variants={accordionItem}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                        >
                          <p className="themes__model-title">Glass Texture</p>
                        </motion.div>
                        <motion.div
                          className="segmented-control"
                          variants={accordionItem}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          transition={{ delay: 0.05 }}
                        >
                          <motion.div className="glass__texture-buttons">
                            {glassBackgroundTextures.map((texture) => (
                              <motion.button
                                whileHover={{
                                  scale: 1.05,
                                }}
                                whileTap={{ scale: 0.95 }}
                                key={texture.id}
                                className={`themes_colors-button ${
                                  selectedGlassTexture === texture.id
                                    ? "themes_colors-button-active"
                                    : ""
                                }`}
                                onClick={() =>
                                  setSelectedGlassTexture(texture.id)
                                }
                                title={texture.name}
                                style={{
                                  border: !texture.url ? "2px solid #000" : "",
                                  backgroundColor: texture.url
                                    ? "transparent"
                                    : "var(--text-color-light)",
                                }}
                              >
                                {texture.url ? (
                                  <motion.img
                                    whileHover={{
                                      borderRadius: "0",
                                    }}
                                    src={texture.url}
                                    alt={texture.name}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: !texture.url
                                        ? "3px"
                                        : selectedGlassTexture === texture.id
                                          ? "0"
                                          : "3px",
                                    }}
                                  />
                                ) : (
                                  <i className="bx bx-block"></i>
                                )}
                              </motion.button>
                            ))}
                          </motion.div>
                        </motion.div>

                        <motion.div
                          className="themes__model-content"
                          variants={accordionItem}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          transition={{ delay: 0.1 }}
                        >
                          <p className="themes__model-title">Glass Intensity</p>
                        </motion.div>

                        {/* Blur Control */}
                        <motion.div
                          variants={accordionItem}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          transition={{ delay: 0.15 }}
                          className="themes__model-content"
                          style={{ justifyContent: "center" }}
                        >
                          <div className="color-mix__container">
                            <span className="glass_label">Blur</span>
                            <input
                              type="range"
                              min="0"
                              max="40"
                              value={glassBlur}
                              onChange={(e) =>
                                setGlassBlur(Number(e.target.value))
                              }
                            />
                            <span className="glass_span">{glassBlur}px</span>
                          </div>
                        </motion.div>

                        {/* Opacity Control */}
                        <motion.div
                          variants={accordionItem}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          transition={{ delay: 0.2 }}
                          className="themes__model-content"
                          style={{ justifyContent: "center" }}
                        >
                          <div className="color-mix__container">
                            <span className="glass_label">Opacity</span>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.05"
                              value={glassOpacity}
                              onChange={(e) =>
                                setGlassOpacity(Number(e.target.value))
                              }
                            />
                            <span className="glass_span">
                              {Math.round(glassOpacity * 100)}%
                            </span>
                          </div>
                        </motion.div>

                        {/* Saturation Control */}
                        <motion.div
                          variants={accordionItem}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          transition={{ delay: 0.25 }}
                          className="themes__model-content"
                          style={{
                            justifyContent: "center",
                            marginBottom: "3px",
                          }}
                        >
                          <div className="color-mix__container">
                            <span className="glass_label">Saturate</span>
                            <input
                              type="range"
                              min="100"
                              max="200"
                              value={glassSaturation}
                              onChange={(e) =>
                                setGlassSaturation(Number(e.target.value))
                              }
                            />
                            <span className="glass_span">
                              {glassSaturation}%
                            </span>
                          </div>
                        </motion.div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Glass Cursor Control */}
              <motion.div
                className={`themes__section ${
                  glassCursorEnabled ? "active__color-mix" : ""
                }`}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={childVariants}
              >
                <motion.div
                  className="themes__model-content"
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <ToggleSwitch
                    id="GlassCursorCheckbox"
                    modelTitle={
                      <>
                        Glass Cursor{" "}
                        <span
                          style={{
                            fontSize: "0.65rem",
                            opacity: 0.5,
                            fontWeight: 400,
                          }}
                        >
                          (Desktop Only)
                        </span>
                      </>
                    }
                    checked={glassCursorEnabled}
                    onChange={handleGlassCursorChange}
                    icons={["bx bx-mouse", "bx bx-pointer"]}
                  />
                </motion.div>
              </motion.div>

              {/** Render Color Mix Section */}
              <motion.div
                className={`themes__section ${
                  useColorMixBg ? "active__color-mix" : ""
                }`}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={childVariants}
              >
                <motion.div
                  className="themes__model-content"
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <ToggleSwitch
                    id="ColorMixBgCheckbox"
                    modelTitle={"BG Color-Mix"}
                    checked={useColorMixBg}
                    onChange={handleColorMixBgChange}
                    icons={["bx bx-color-fill", "bx bx-palette"]}
                    isExpanded={expandedSection.has("colorMix")}
                    onToggleExpand={
                      useColorMixBg
                        ? () => toggleSection("colorMix")
                        : undefined
                    }
                  />
                </motion.div>

                <AnimatePresence mode="wait">
                  {useColorMixBg && expandedSection.has("colorMix") && (
                    <motion.div
                      variants={accordionContainer}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <motion.div
                        variants={accordionItem}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="themes__model-content"
                        style={{ margin: "3px 0" }}
                      >
                        <div className="color-mix__container">
                          <span className="glass_label">Saturate</span>
                          <input
                            type="range"
                            min={20}
                            max={50}
                            value={colorMixPercent}
                            onChange={(e) =>
                              setColorMixPercent(Number(e.target.value))
                            }
                          />
                          <span
                            className="color-mix-value"
                            style={{
                              width: "25px",
                              fontSize: "0.75rem",
                              fontWeight: "500",
                            }}
                          >
                            {colorMixPercent}%
                          </span>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              <ColorPicker
                activeThemeInCustomThemes={activeThemeInCustomThemes}
                setActiveThemeInCustomThemes={setActiveThemeInCustomThemes}
                applyCustomTheme={applyCustomTheme}
                storedCustomThemes={storedCustomThemes}
                storedDefaultThemes={storedDefaultThemes}
                applyDefaultTheme={applyDefaultTheme}
                activeThemeInDefaultThemes={activeThemeInDefaultThemes}
                setActiveThemeInDefaultThemes={setActiveThemeInDefaultThemes}
                colors={colors}
                setColors={setColors}
                enableColors={enableColors}
                setEnableColors={setEnableColors}
                expandedSection={expandedSection}
                setExpandedSection={setExpandedSection}
              />

              <motion.div
                className="themes__actions"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={childVariants}
              >
                {isAdmin && (
                  <div className="themes__action themes__action--edit">
                    <ButtonComponent
                      buttonTitle="Open the edit default themes modal"
                      onClick={() => setIsEditModalOpen(true)}
                      className="themes__reset-preferences-btn"
                    >
                      <i
                        className="bx bx-edit"
                        style={{ fontSize: "1rem" }}
                      ></i>
                      Edit Default Themes
                    </ButtonComponent>
                  </div>
                )}

                <div className="themes__action themes__action--restore">
                  <ButtonComponent
                    buttonTitle="Reset all theme settings to default and reload"
                    onClick={handleResetThemeSettings}
                    className="themes__reset-preferences-btn"
                  >
                    <i
                      className="bx bx-reset"
                      style={{ fontSize: "0.8rem" }}
                    ></i>
                    {!isAdmin && "Reset theme settings"}
                  </ButtonComponent>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Edit Default Themes Modal */}
      <EditDefaultThemesModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        currentThemes={
          defaultThemesDraft?.themes || firebaseThemesData?.themes || []
        }
        onSave={saveDefaultThemesDraft}
      />
    </>
  );
};

export default Themes;
